import { useState, useEffect } from 'react';
import { TrendingUp, ArrowUpRight } from 'lucide-react';
import { workspaceService } from '../lib/services';

interface ROIData {
    timeSavedSec: number;
    timeSavedHours: number;
    timeWorkedSec: number;
    moneySavedCents: number;
    moneySavedEur: number;
    replicaCostCents: number;
    roiMultiplier: number;
    tasksCompleted: number;
    workflowRuns: number;
    lastUpdatedAt: string;
}

interface ROIIndicatorProps {
    /** Compact mode for grid placement (company dashboard) */
    compact?: boolean;
}

export default function ROIIndicator({ compact = false }: ROIIndicatorProps) {
    const [roi, setRoi] = useState<ROIData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        workspaceService.getRoi().then(res => {
            if (res.data?.roi) {
                setRoi(res.data.roi);
            }
        }).catch(() => { }).finally(() => setLoading(false));
    }, []);

    // Format time display
    const formatTime = (hours: number): string => {
        if (hours >= 24) {
            const days = Math.floor(hours / 24);
            const remainingHours = Math.round(hours % 24);
            return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
        }
        if (hours >= 1) return `${Math.round(hours)}h`;
        const minutes = Math.round(hours * 60);
        return minutes > 0 ? `${minutes}m` : '0m';
    };

    // Format money display
    const formatMoney = (eur: number): string => {
        if (eur >= 1000) return `€${(eur / 1000).toFixed(1)}k`;
        return `€${Math.round(eur)}`;
    };

    // ── Compact mode (for Company Dashboard 2×2 grid) ──
    if (compact) {
        return (
            <div className="card p-4">
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <TrendingUp size={16} className="text-emerald-400" />
                    </div>
                    {roi && roi.roiMultiplier > 0 && roi.roiMultiplier !== Infinity && (
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-0.5">
                            <ArrowUpRight size={8} />
                            {roi.roiMultiplier}×
                        </span>
                    )}
                </div>
                {loading ? (
                    <div className="animate-pulse">
                        <div className="h-6 bg-hig-bg-tertiary rounded w-16 mb-1" />
                        <div className="h-3 bg-hig-bg-tertiary rounded w-20" />
                    </div>
                ) : (
                    <>
                        <p className="text-xl font-bold text-hig-text-primary">
                            {roi ? formatMoney(roi.moneySavedEur) : '€0'}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-hig-text-secondary font-medium mt-1">
                            ROI Value Saved
                        </p>
                    </>
                )}
            </div>
        );
    }

    // ── Full mode (for Personal Dashboard governance grid) ──
    return (
        <div className="bg-hig-bg-tertiary/50 rounded-lg px-3 py-2.5 border border-hig-divider relative overflow-hidden">
            {/* Subtle gradient accent */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.03] to-transparent pointer-events-none" />

            <p className="text-[9px] uppercase tracking-widest text-hig-text-tertiary font-bold mb-1 relative z-10">
                ROI
            </p>
            {loading ? (
                <div className="animate-pulse relative z-10">
                    <div className="h-5 bg-hig-bg-tertiary rounded w-14" />
                </div>
            ) : (
                <div className="relative z-10">
                    <div className="flex items-center gap-1.5">
                        <TrendingUp size={14} className="text-emerald-400" />
                        <span className="text-sm font-semibold text-hig-text-primary">
                            {roi ? formatTime(roi.timeSavedHours) : '0m'}
                        </span>
                    </div>
                    {roi && roi.moneySavedEur > 0 && (
                        <p className="text-[9px] text-emerald-400/70 mt-0.5 font-medium">
                            ≈ {formatMoney(roi.moneySavedEur)} saved
                        </p>
                    )}
                    {roi && roi.roiMultiplier > 0 && roi.roiMultiplier !== Infinity && (
                        <div className="mt-1 flex items-center gap-0.5">
                            <span className="text-[8px] font-bold px-1 py-px rounded bg-emerald-500/10 text-emerald-400">
                                {roi.roiMultiplier}× ROI
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
